<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.city, 'Oraș')"
      :items="cities"
      item-title="name"
      item-value="id"
      name="city"
      :loading="loading"
      :disabled="loading"
      v-model="selectedCity"
      :rules="[v => !!v || translation(translationList.required, 'Acest câmp este obligatoriu')]"
      return-object
      :menu-props="{ eager: true }"
  >
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useAddressStore } from '~/plugins/store/address';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type { City } from '~/types/Address';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => City | null,
      default: () => null,
      required: false
    },
    countyId: {
      type: Number as () => number | null,
      required: false
    },
    addressIndex: Number as () => number | null,
  },
  emits: ['update:modelValue', 'validation-address-status'],
  setup(props, { emit }) {
    const { modelValue, countyId, addressIndex } = toRefs(props);
    const addressStore = useAddressStore();
    const translationStore = useTranslationStore();
    const selectedCity = ref(modelValue.value ? modelValue.value : null);
    const cities = ref<City[]>([]);
    const translationList = ref<TranslationList>({});
    const loading = ref(false);
    let initialSetupDone = false;
    const previousCountyId = ref(countyId.value);
    const error = ref([]);

    const fetchCities = async (countyId: number) => {
      loading.value = true;
      if (countyId) {
        cities.value = await addressStore.getCities(countyId);
      } else {
        cities.value = [];
        selectedCity.value = null;
        emit('update:modelValue', null);
      }
      loading.value = false;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (countyId.value) {
        await fetchCities(countyId.value);
      }
      initialSetupDone = true;
      const isValid = !!selectedCity.value;
      emit('validation-address-status', {
        field: Number.isInteger(addressIndex.value) ? `city-${addressIndex.value}` : 'city',
        isValid
      });

      // Listen for validate events
      window.addEventListener('validate-address', handleValidationEvent);
      window.addEventListener('validate-travel', handleValidationEvent);
      window.addEventListener('validate-house', handleValidationEvent);
    });

    const handleValidationEvent = (data: any) => {
      if (data.detail && data.detail === (Number.isInteger(addressIndex.value) ? `city-${addressIndex.value}` : 'city')) {
        if (!selectedCity.value) {
          error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        } else {
          error.value = [];
          emit('validation-address-status', {
            field: Number.isInteger(addressIndex.value) ? `city-${addressIndex.value}` : 'city',
            isValid: true
          });
        }
      }
    };

    watch(countyId, async (newCountyId) => {
      if (newCountyId !== previousCountyId.value) {
        if (previousCountyId.value && selectedCity.value) {
          selectedCity.value = null;
          emit('update:modelValue', null);
        }
        if (newCountyId) {
          await fetchCities(newCountyId);
        }
        previousCountyId.value = newCountyId; // Actualizează ID-ul județului anterior
      }
    });

    watch(modelValue, (newValue) => {
      if (newValue) {
        selectedCity.value = newValue;
      } else if (!newValue && countyId.value === previousCountyId.value) {
        selectedCity.value = null;
      }

      const isValid = !!newValue;
      emit('validation-address-status', {
        field: Number.isInteger(addressIndex.value) ? `city-${addressIndex.value}` : 'city',
        isValid
      });
    });

    watch(selectedCity, (newCity) => {
      error.value = [];
      if (initialSetupDone && (newCity?.id !== modelValue.value?.id)) {
        emit('update:modelValue', newCity);
      }
    });

    return {
      selectedCity,
      translationList,
      translation,
      cities,
      loading,
      error,
    };
  },
});
</script>

<style scoped>
</style>